import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Eye,
  Server,
  CheckCircle,
  ArrowRight,
  Camera,
  Monitor,
  Key,
  AlertTriangle,
  ShieldCheck,
  Database,
} from "lucide-react";

const Security = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: "Data Encryption",
      description:
        "All data is encrypted in transit using HTTPS/TLS and stored securely on Google Cloud Platform (Firebase) with industry-standard encryption.",
    },
    {
      icon: Shield,
      title: "Firebase Security",
      description:
        "Built on Google Cloud Platform with enterprise-grade security, automatic security updates, and DDoS protection.",
    },
    {
      icon: Key,
      title: "Secure Authentication",
      description:
        "Firebase Authentication with email/password and Google OAuth, ensuring secure user access and identity verification.",
    },
    {
      icon: Eye,
      title: "Role-Based Access Control",
      description:
        "Granular permissions ensure only authorized users (HR, Job Seekers, Admins) can access relevant data and features.",
    },
    {
      icon: Server,
      title: "Cloud Infrastructure",
      description:
        "Hosted on Google Cloud Platform with automatic backups, high availability, and 99.9% uptime SLA.",
    },
    {
      icon: Database,
      title: "Secure Data Storage",
      description:
        "Firestore database with security rules, data validation, and automatic scaling to protect your information.",
    },
  ];

  const proctoringFeatures = [
    {
      icon: Camera,
      title: "Camera Monitoring",
      description: "Real-time camera monitoring during tests to prevent cheating and ensure test integrity.",
    },
    {
      icon: Monitor,
      title: "Tab Switch Detection",
      description: "Automatically detects when candidates switch tabs or windows during assessments.",
    },
    {
      icon: ShieldCheck,
      title: "Copy/Paste Blocking",
      description: "Prevents copying and pasting content during tests to maintain assessment security.",
    },
    {
      icon: AlertTriangle,
      title: "Violation Tracking",
      description: "Comprehensive violation tracking system that monitors and records suspicious activities.",
    },
  ];

  const dataProtection = [
    "All candidate data is encrypted using industry-standard encryption",
    "Data stored securely on Google Cloud Platform (Firebase)",
    "Automatic backups with point-in-time recovery capabilities",
    "Role-based access control ensures data privacy",
    "Secure API endpoints with authentication requirements",
    "Regular security updates and patches",
  ];

  const privacyPrinciples = [
    {
      title: "Data Minimization",
      description:
        "We only collect data that's necessary for providing our recruitment services.",
    },
    {
      title: "Access Control",
      description:
        "Strict role-based permissions ensure only authorized users can access sensitive information.",
    },
    {
      title: "Transparency",
      description:
        "Clear privacy policies and terms of service explain how data is collected and used.",
    },
    {
      title: "Data Security",
      description:
        "Multiple layers of security protect your data from unauthorized access.",
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
          <div className="flex items-center">
            <Link
              to="/login"
              className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Security & Privacy
            <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent">
              Built In
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
            Your data security is our top priority. VetriaAI implements industry-leading security measures to protect your sensitive recruitment data.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/login"
              className="group w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/features"
              className="group w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center"
            >
              View Features
            </Link>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Security Framework</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Comprehensive Security Measures
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Multiple layers of protection to ensure your recruitment data stays secure
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proctoring Security */}
      <section className="py-12 sm:py-16 md:py-24 bg-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Test Security</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Advanced Proctoring System
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              10-layer security system to ensure test integrity and prevent cheating
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {proctoringFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-5 sm:p-6 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2 text-center"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm px-2">{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 sm:mt-12 bg-white rounded-card p-6 sm:p-8 shadow-card border border-gray-100">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-4 text-center">
              Additional Proctoring Measures
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-gray-600">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Keyboard shortcut blocking (20+ shortcuts)</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Fullscreen enforcement</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Right-click disable</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Window focus monitoring</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Real-time violation tracking</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">Auto-termination after violations</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Protection */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
                <span className="text-xs sm:text-sm font-semibold text-primary">Data Protection</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Data Protection & Privacy
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8">
                We implement comprehensive data protection measures to ensure your candidate information is secure, private, and handled with care.
              </p>
              <ul className="space-y-3 sm:space-y-4">
                {dataProtection.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-6 sm:p-8 rounded-card mt-8 lg:mt-0">
              <div className="text-center">
                <Server className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-3 sm:mb-4" />
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
                  Google Cloud Platform
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Built on enterprise-grade infrastructure with automatic failover, 24/7 monitoring, and high availability.
                </p>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center">
                  <div className="bg-white rounded-card p-3 sm:p-4">
                    <div className="text-xl sm:text-2xl font-bold text-primary">24/7</div>
                    <div className="text-xs sm:text-sm text-gray-600">Monitoring</div>
                  </div>
                  <div className="bg-white rounded-card p-3 sm:p-4">
                    <div className="text-xl sm:text-2xl font-bold text-success">99.9%</div>
                    <div className="text-xs sm:text-sm text-gray-600">Uptime SLA</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Principles */}
      <section className="py-12 sm:py-16 md:py-24 bg-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Privacy First</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Privacy by Design
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Privacy and data protection are built into every aspect of our platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {privacyPrinciples.map((principle, index) => (
              <div
                key={index}
                className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2"
              >
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                  {principle.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Best Practices */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-primary/5 border border-primary/20 rounded-card p-6 sm:p-8">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-primary mr-3 sm:mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
                  Security Best Practices
                </h3>
                <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                  We continuously work to improve our security posture:
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                  <li>• Regular security audits and vulnerability assessments</li>
                  <li>• Automatic security updates and patches</li>
                  <li>• Secure coding practices and code reviews</li>
                  <li>• Employee security training and awareness</li>
                  <li>• Incident response plan for security events</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">Security You Can Trust</h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 opacity-95 max-w-2xl mx-auto px-4">
            Join early adopters who trust VetriaAI with their recruitment data.
          </p>
          <Link
            to="/login"
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get Started
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link to="/" className="mb-4 inline-block flex items-center gap-0 -ml-1">
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Security;
